import React, { useMemo, useState } from 'react';
import { Image, Skeleton } from 'antd';
import { klona } from 'klona';
import { Icon } from '@fuxi/eevee-icon';
import { PhysicalInfoDimension } from 'common-package';
import { PHYSICAL_CONDITION_ITEMS } from '@/pages/ai-consultation/components/physical-condition';
import { PAIN_AREA_CONFIG } from '@/pages/ai-consultation/components/mark-pain-area';
import { BACK_AREA, SHOULDER_NECK_AREA } from '@/pages/ai-consultation/components/result-plan';
import { POSTURE_ADJUST_ITEM_2_TEXT } from '@/pages/ai-consultation/components/posture-condition';
import { formatNumber } from '@/pages/user/member-management/components/AIConsultationInfo/components/WeightLoss/WeightLossItem';
import { storagePrefixV2 } from '@/constant';
import cx from './index.module.scss';

const PREVIEW_IMG = storagePrefixV2 + '/view-pain.png';
export const BODY_BG_IMG = storagePrefixV2 + '/ai-consulation/mark-pain-body-bg.png';

// 您的体质数据过滤并排序
const AI_PHYSICAL_ASSESSMENT = ['阳虚质', '阴虚质', '气虚质', '痰湿质', '湿热质', '血瘀质', '特禀质', '气郁质'];
export const findMatchingElements = (arr: string[]) => {
  return AI_PHYSICAL_ASSESSMENT?.filter(item => arr?.some(itm => item.startsWith(itm)));
};
interface Props {
  customerInfo: GetCustomerProfileResponse;
  loading: boolean;
}
const WEIGHT_DIMENSION_TYPE = [
  'waistCircumference',
  'backThickness',
  'leftArmCircumference',
  'rightArmCircumference',
  'leftThighCircumference',
  'rightThighCircumference',
  'leftCalfCircumference',
  'rightCalfCircumference',
];

const THERAPY_TYPE_RESULT_TITLE: Record<AIDiagnose.TherapyType, string> = {
  weightLoss: '围度数据',
  bodyAdjust: '体态问题',
  painRelief: '痛症位置',
  physicalImprove: '体质问题',
};

export const THERAPY_TYPE_SORT = ['physicalImprove', 'painRelief', 'bodyAdjust', 'weightLoss'];

const DetectionResult: React.FC<Props> = ({ customerInfo, loading }) => {
  const [visiblePain, setVisiblePain] = useState<boolean>(false);
  const [visibleWeightLoss, setVisibleWeightLoss] = useState<boolean>(false);
  // 展示痛症舒缓选中区域
  const currentMarkPainAreaShowName = useMemo(
    () =>
      Array.from(
        new Set(
          customerInfo?.painReliefList?.map(item =>
            SHOULDER_NECK_AREA.includes(item.problem) ? '肩部' : BACK_AREA.includes(item?.problem) ? '背部' : '腰部'
          )
        )
      )
        ?.sort((a, b) => ['肩部', '背部', '腰部']?.indexOf(a) - ['肩部', '背部', '腰部']?.indexOf(b))
        ?.join('、') || [],
    [customerInfo?.painReliefList]
  );

  const markPainArea = useMemo(
    () => customerInfo?.painReliefList?.map(item => item.problem),
    [customerInfo?.painReliefList]
  );

  // 融合方案排序
  const hopeTypesSort = useMemo(() => {
    const newHopeTypes = klona(customerInfo?.hopeTypes);
    return newHopeTypes?.sort((a, b) => THERAPY_TYPE_SORT?.indexOf(a) - THERAPY_TYPE_SORT?.indexOf(b)) || [];
  }, [customerInfo?.hopeTypes]);

  // 围度目标排序
  const weightLossList = useMemo(() => {
    const newWeightLossList = klona(customerInfo?.weightLossList);
    return (
      newWeightLossList?.sort(
        (a, b) => WEIGHT_DIMENSION_TYPE?.indexOf(a?.problem) - WEIGHT_DIMENSION_TYPE?.indexOf(b?.problem)
      ) || []
    );
  }, [customerInfo?.weightLossList]);

  return (
    <div className={cx('detection-result-wrapper')}>
      <Skeleton loading={loading} title={false} paragraph={{ rows: 0 }}>
        <div className={cx('info-wrapper')}>
          {!!customerInfo?.physicalAssessment?.length && (
            <div className={cx('problem-item')}>
              <div className={cx('label')}>您的体质</div>
              <div className={cx('value', 'physical-content')}>
                {(
                  (customerInfo?.tongueDiagnose
                    ? customerInfo?.physicalAssessment
                    : findMatchingElements(customerInfo?.physicalAssessment)) || []
                ).join('、')}
              </div>
            </div>
          )}

          {hopeTypesSort?.map(item => (
            <div className={cx('problem-item')} key={item}>
              <div className={cx('label')}>{THERAPY_TYPE_RESULT_TITLE[item]}</div>
              <div className={cx('value')}>
                {item === 'painRelief' ? (
                  <div className={cx('pain-item')}>
                    <div>{currentMarkPainAreaShowName}</div>
                    {currentMarkPainAreaShowName?.length > 0 && (
                      <div className={cx('view-info')} onClick={() => setVisiblePain(true)}>
                        <span>查看图示</span>
                        <Icon name="view-info" className={cx('icon')} />
                        <Image
                          src={PREVIEW_IMG}
                          alt="preview-img"
                          preview={{
                            visible: visiblePain,
                            onVisibleChange: (visible: boolean) => setVisiblePain(visible),
                            maxScale: 5,
                            styles: {
                              mask: {
                                backgroundColor: `rgba(0, 0, 0, 0.40)`,
                              },
                            },
                            imageRender: (originalNode, info) => {
                              return (
                                <div className={cx('preview-image')}>
                                  <div className={cx('img-wrapper')}>
                                    <img className={cx('main-body-bg')} src={BODY_BG_IMG} alt="" />
                                    <div className={cx('pain-area-wrapper')}>
                                      {Object.keys(PAIN_AREA_CONFIG).map(item => (
                                        <div
                                          className={cx('marker-item', item)}
                                          key={item}
                                          style={{
                                            backgroundImage: `url(${
                                              PAIN_AREA_CONFIG[item as AIDiagnose.MarkPainArea]?.[
                                                markPainArea?.includes(item as AIDiagnose.MarkPainArea)
                                                  ? 'selectUrl'
                                                  : 'defaultPlainUrl'
                                              ]
                                            })`,
                                          }}
                                        ></div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          }}
                          className={cx('img')}
                        />
                      </div>
                    )}
                  </div>
                ) : item === 'bodyAdjust' ? (
                  <>
                    {customerInfo?.bodyAdjustList
                      ?.sort(
                        (a, b) =>
                          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(a?.problem) -
                          Object.keys(POSTURE_ADJUST_ITEM_2_TEXT)?.indexOf(b?.problem)
                      )
                      ?.map(item => POSTURE_ADJUST_ITEM_2_TEXT[item?.problem])
                      ?.join('、')}
                  </>
                ) : item === 'weightLoss' ? (
                  <div className={cx('weight-wrapper')}>
                    <div className={cx('view-info')} onClick={() => setVisibleWeightLoss(true)}>
                      <span>查看详情</span>
                      <Icon name="view-info" className={cx('icon')} />
                      <Image
                        src={PREVIEW_IMG}
                        alt="preview-img"
                        preview={{
                          visible: visibleWeightLoss,
                          onVisibleChange: (visible: boolean) => setVisibleWeightLoss(visible),
                          maxScale: 5,
                          imageRender: (originalNode, info) => {
                            return (
                              <div className={cx('weight-loss-preview')}>
                                <div className={cx('weight-loss-list')}>
                                  {weightLossList?.map(item => (
                                    <div key={item?.problem} className={cx('weight-loss-item')}>
                                      <div className={cx('current')}>
                                        <span>当前</span>
                                        <span className={cx('value')}>{formatNumber(item?.currentValue)}</span>
                                        <span>厘米</span>
                                      </div>
                                      <div className={cx('part')}>{PhysicalInfoDimension[item?.problem]}</div>
                                      <div className={cx('goal', { 'no-goal': !item?.goalValue })}>
                                        <span>目标</span>
                                        {item?.goalValue ? (
                                          <>
                                            <span className={cx('value')}>{formatNumber(item?.goalValue)}</span>
                                            <span>厘米</span>
                                          </>
                                        ) : (
                                          <span className={cx('no-data')}>暂无</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          },
                        }}
                        className={cx('img')}
                      />
                    </div>
                  </div>
                ) : item === 'physicalImprove' ? (
                  <>
                    {customerInfo?.physicalImproveList
                      ?.sort(
                        (a, b) =>
                          Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(a?.problem) -
                          Object.keys(PHYSICAL_CONDITION_ITEMS)?.indexOf(b?.problem)
                      )
                      ?.filter(
                        item =>
                          //@ts-ignore
                          item.problem !== 'stayUpLate'
                      )
                      ?.map(item => PHYSICAL_CONDITION_ITEMS[item.problem]?.name)
                      ?.join('、')}
                  </>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div className={cx('bottom')}>
          <Icon name="tips" className={cx('icon')} />
          <div className={cx('text')}>部分内容来自AI问询</div>
        </div>
      </Skeleton>
    </div>
  );
};

export default DetectionResult;
